import { Box, FormControl, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import { useEffect } from 'react';
import { type UseFormSetValue, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { NumberField } from '../../../../../components/number-field';
import { UnitAutocomplete } from '../../../../../components/unit-autocomplete';
import type { AddItemFormData } from '../../../hooks/use-add-item-form';
import type { ExtractedItemData } from '../../../hooks/use-item-extraction';
import { useProductNames } from '../../../hooks/use-product-names';
import { useExtractedData } from '../../extracted-data-context';
import { LineAmountWarning } from '../../line-amount-warning';
import { ProductNameAutocomplete } from '../../product-name-autocomplete';
import { useShouldHighlightAiGaps } from '../ai-gap-banner';
import { SectionHeader } from '../section-header';

function applyExtractedBasicData(
  consumed: Pick<ExtractedItemData, 'productName' | 'quantity' | 'unit'> | null,
  setValue: UseFormSetValue<AddItemFormData>,
) {
  if (!consumed) {
    return;
  }
  if (consumed.productName) {
    setValue('productName', consumed.productName, { shouldValidate: true });
  }
  if (consumed.quantity) {
    setValue('quantity', consumed.quantity, { shouldValidate: false });
  }
  if (consumed.unit) {
    setValue('unit', consumed.unit, { shouldValidate: false });
  }
}

export function BasicDataStep() {
  const { t } = useTranslation('goodsReceiving');
  const {
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<AddItemFormData>();
  const { data: productNames = [], isLoading: isLoadingProductNames } = useProductNames();
  const { extractedData, consumeFields } = useExtractedData();

  const productName = watch('productName');
  const quantity = watch('quantity');
  const unit = watch('unit');

  useEffect(() => {
    if (!extractedData) {
      return;
    }
    const consumed = consumeFields(['productName', 'quantity', 'unit']);
    applyExtractedBasicData(consumed, setValue);
  }, [extractedData, setValue, consumeFields]);

  const productNameError = errors.productName?.message as string | undefined;
  const shouldHighlightAiGaps = useShouldHighlightAiGaps();
  const productNameAiGap = shouldHighlightAiGaps && !productName?.trim();
  const productNameHint = productNameError ?? (productNameAiGap ? t('aiGap.fieldHint') : undefined);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <SectionHeader title={t('basicDataTitle')} helper={t('basicDataHelper')} />
      <ProductNameAutocomplete
        value={productName || ''}
        onChange={(value) => setValue('productName', value, { shouldValidate: true })}
        options={productNames}
        isLoading={isLoadingProductNames}
        warning={productNameAiGap}
      />
      {productNameHint && (
        <Typography
          variant="caption"
          sx={{ color: productNameError ? 'error.main' : 'warning.main' }}
        >
          {productNameHint}
        </Typography>
      )}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <NumberField
          label={t('quantityLabel')}
          value={quantity ? Number(quantity) : null}
          onValueChange={(val) =>
            setValue('quantity', val !== null ? String(val) : '', { shouldValidate: false })
          }
          min={0}
          step={0.01}
          fullWidth
        />
        <UnitAutocomplete
          value={unit || ''}
          onChange={(val) => setValue('unit', val, { shouldValidate: false })}
          fullWidth
        />
      </Box>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <NumberField
          label={t('unitPriceLabel')}
          value={watch('unitPrice') ? Number(watch('unitPrice')) : null}
          onValueChange={(val) =>
            setValue('unitPrice', val !== null ? String(val) : '', { shouldValidate: false })
          }
          min={0}
          step={0.01}
          fullWidth
        />
        <FormControl fullWidth>
          <InputLabel>{t('currencyLabel')}</InputLabel>
          <Select
            value="EUR"
            label={t('currencyLabel')}
            onChange={(e) => setValue('currency', e.target.value, { shouldValidate: false })}
          >
            <MenuItem value="EUR">EUR</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <NumberField
        label={t('lineTotalLabel')}
        value={watch('lineTotal') ? Number(watch('lineTotal')) : null}
        onValueChange={(val) =>
          setValue('lineTotal', val !== null ? String(val) : '', { shouldValidate: false })
        }
        min={0}
        step={0.01}
        fullWidth
      />
      <LineAmountWarning
        unitPrice={parseFormAmount(watch('unitPrice'))}
        quantity={parseFormAmount(quantity)}
        lineTotal={parseFormAmount(watch('lineTotal'))}
      />
    </Box>
  );
}

function parseFormAmount(value: string | undefined): number | null {
  return value ? Number(value) : null;
}
